import styled from 'styled-components';
import { ContentContainer } from '../containers';
import { SectionTitle } from '../Title';
import COLORS from '../../colors';
import {
  MONTH_LOWERCASE,
  MONTH_PASCAL,
  TVNZ_LOGO_IMAGE_PATH
} from '../../constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setAdMirroringLoading,
  toggleAdMirroring
} from '../../redux/features/calculated/calculatedSlice';
import { mapPascalMonthToLowercaseMonth } from '../../util';
import { useEffect, useState } from 'react';
import { getAdMirroringLoading } from '../../services/calculate';
import {
  AD_MIRRORING_DISABLED,
  AD_MIRRORING_TITLE_AVAILABLE,
  AD_MIRRORING_TITLE_NOT_AVAILABLE
} from './constants';
import { Card } from '../Card';
import { Antenna } from '../Icons';

interface AdMirroringProps {
  months: MONTH_PASCAL[] | undefined;
  batchUpdateTotals: (
    month: MONTH_LOWERCASE,
    adMirroringLoading: number
  ) => void;
}

const Logo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const DisabledText = styled.div`
  width: 600px;
  position: relative;
  font-size: 16px;
  color: ${COLORS.ERROR};
  line-height: 24px;
  text-align: left;
  display: inline-block;
`;

const PlatformOptionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 0%;
  padding-bottom: 32px;
  padding-top: 32px;
  font-size: 16px;
  background-color: transparent;
`;

const AdMirroring = ({ months, batchUpdateTotals }: AdMirroringProps) => {
  const dispatch = useAppDispatch();
  const demographic = useAppSelector((state) => state.detail.demographic);
  const calculated = useAppSelector((state) => state.calculated);
  const adMirroringLoadingSheet = useAppSelector(
    (state) => state.spreadsheet.adMirroring
  );

  const [isToggled, setIsToggled] = useState<boolean>(false);
  const [loading, setLoading] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const handleToggle = () => {
    const newIsToggled = !isToggled;
    setIsToggled(newIsToggled);

    months?.forEach((month) => {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
      dispatch(
        toggleAdMirroring({
          month: lowercaseMonth,
          isToggled: newIsToggled
        })
      );
      batchUpdateTotals(lowercaseMonth, newIsToggled ? loading : 0);
    });
  };

  const setUpPageOnLoad = () => {
    if (!months || !demographic) return;

    const lowercaseMonth = mapPascalMonthToLowercaseMonth(months[0]);
    const adMirroringEnabled =
      calculated[lowercaseMonth].adMirroring.isAdMirroringEnabled;

    const loading =
      getAdMirroringLoading(adMirroringLoadingSheet, demographic) * 100;

    months?.forEach((month) => {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);
      dispatch(
        setAdMirroringLoading({
          adMirroringLoading: loading,
          month: lowercaseMonth
        })
      );
    });

    if (loading <= 0) setIsDisabled(true);
    setLoading(loading);
    setIsToggled(adMirroringEnabled);
  };

  useEffect(() => {
    setUpPageOnLoad();
  }, []);

  return (
    <ContentContainer isDisabled={isDisabled}>
      <SectionTitle
        title={
          isDisabled
            ? AD_MIRRORING_TITLE_NOT_AVAILABLE
            : AD_MIRRORING_TITLE_AVAILABLE
        }
        loadingPercentage={isToggled ? loading : 0}
      />
      {isDisabled && (
        <DisabledText>
          {AD_MIRRORING_DISABLED} {demographic}
        </DisabledText>
      )}
      <PlatformOptionContainer>
        <Card
          title={'Broadcast Only'}
          supportingText={
            'Reach audiences through traditional broadcast delivery. Serve your schedule directly into TVNZ’s linear TV channels for nationwide impact.'
          }
          icon={<Antenna />}
          checked={!isToggled}
          value={0}
          onChange={handleToggle}
          backgroundColor={COLORS.NEUTRAL_900}
          iconAlignment="center"
        />
        <Card
          title={'Cross Platform'}
          supportingText={
            'Reach more people more effectively using TVNZ Ad Mirroring. Digitally serving broadcast schedule into TVNZ+ livestream channels.'
          }
          icon={[<Antenna />, <Logo src={TVNZ_LOGO_IMAGE_PATH.PLUS} />]}
          checked={isToggled}
          value={0}
          onChange={handleToggle}
          backgroundColor={COLORS.NEUTRAL_900}
          iconAlignment="center"
        />
      </PlatformOptionContainer>
    </ContentContainer>
  );
};

export default AdMirroring;
